import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

type FAQItem = {
  question: string;
  answer: React.ReactNode;
  category: string;
};

const FAQ_ITEMS: FAQItem[] = [
  {
    category: "General Questions",
    question: "What is StoryMuse.io?",
    answer: "StoryMuse.io is a collaborative platform where writers share their book ideas, and readers engage by voting, commenting, and participating in polls. Together, we shape the stories that could become tomorrow's bestsellers."
  },
  {
    category: "General Questions",
    question: "Who can join StoryMuse.io?",
    answer: "Anyone! Whether you're a writer looking for feedback on your ideas or a reader who loves discovering and supporting new stories, StoryMuse.io is open to everyone. Simply sign up as a Reader or Writer to get started."
  },
  {
    category: "General Questions",
    question: "Is StoryMuse.io free to use?",
    answer: "Yes! Joining and using the basic features of StoryMuse.io is completely free for both readers and writers. Additional premium features may be introduced in the future."
  },
  {
    category: "For Writers",
    question: "How is my intellectual property protected?",
    answer: <>We take the protection of your creative ideas very seriously. You retain full ownership of any content you post on StoryMuse.io, and our platform prohibits the unauthorized use or reproduction of user-generated content. Learn more on our <Link to="/ip-protection" className="text-indigo-600 hover:text-indigo-800">Your Ideas Are Protected</Link> page.</>
  },
  {
    category: "For Writers",
    question: "What kind of book ideas should I post?",
    answer: "You can post any book idea you're passionate about, whether it's a rough concept, a detailed synopsis, or even an experimental project. Use the platform to gauge interest, gather feedback, and refine your vision."
  },
  {
    category: "For Writers",
    question: "Can I edit or delete my ideas after posting?",
    answer: "Absolutely. You can edit or delete your ideas at any time from your dashboard. However, be aware that once deleted, ideas cannot be recovered."
  },
  {
    category: "For Readers",
    question: "How do I vote for a book idea?",
    answer: "To vote, simply click the \"Upvote\" button on the book idea that resonates with you. Your votes help writers see which ideas have the most potential."
  },
  {
    category: "For Readers",
    question: "Can I leave feedback for authors?",
    answer: "Yes! Readers can leave comments on any book idea to share their thoughts, ask questions, or encourage the author. Keep your feedback respectful and constructive."
  },
  {
    category: "For Readers",
    question: "Can I see which ideas are trending?",
    answer: "Yes! Our homepage features trending ideas, popular book concepts, and the newest submissions so you can explore the most exciting content."
  }
];

export default function FAQAccordion() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const categories = Array.from(new Set(FAQ_ITEMS.map(item => item.category)));

  const toggleItem = (question: string) => {
    setOpenItems(prev => ({
      ...prev,
      [question]: !prev[question]
    }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4">
      <h2 className="text-3xl font-bold text-center mb-8" style={{ color: 'var(--color-font)' }}>
        Frequently Asked Questions
      </h2>
      <div className="space-y-8">
        {categories.map(category => (
          <div key={category}>
            <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--color-font)' }}>
              {category}
            </h3>
            <div className="space-y-4">
              {FAQ_ITEMS.filter(item => item.category === category).map(({ question, answer }) => (
                <div key={question} className="border rounded-lg" style={{ borderColor: 'var(--color-accent3)' }}>
                  <button
                    onClick={() => toggleItem(question)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:opacity-80"
                    style={{ color: 'var(--color-font)' }}
                  >
                    <span className="font-medium">{question}</span>
                    {openItems[question] ? (
                      <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-accent1)' }} />
                    ) : (
                      <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-accent1)' }} />
                    )}
                  </button>
                  {openItems[question] && (
                    <div className="px-6 py-4 border-t" style={{ borderColor: 'var(--color-accent3)' }}>
                      <p style={{ color: 'var(--color-font)' }}>{answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}